import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Plus, 
  Search, 
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Filter,
  Ticket,
  MessageSquare,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const ticketTypes = [
  { value: "seo", label: "Optimisation SEO" },
  { value: "modification_texte", label: "Modification de texte" },
  { value: "ajout_page", label: "Ajout de page" },
  { value: "ajout_ville", label: "Ajout de ville" },
  { value: "bug", label: "Correction de bug" },
  { value: "vitesse", label: "Optimisation vitesse" },
  { value: "google_ads", label: "Demande Google Ads" },
  { value: "autre", label: "Autre demande" },
];

const priorityColors = {
  low: "bg-[#64748b]/20 text-[#94A3B8] border-[#64748b]/30",
  medium: "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30",
  high: "bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30",
};

const statusConfig = {
  open: { icon: AlertCircle, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", label: "Ouvert" },
  in_progress: { icon: Clock, color: "text-[#3B82F6]", bg: "bg-[#3B82F6]/10", label: "En cours" },
  resolved: { icon: CheckCircle, color: "text-[#10B981]", bg: "bg-[#10B981]/10", label: "Résolu" },
  closed: { icon: XCircle, color: "text-[#64748b]", bg: "bg-[#64748b]/10", label: "Fermé" },
};

export default function TicketsPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  
  // New ticket form
  const [newTicket, setNewTicket] = useState({
    type: "",
    priority: "medium",
    title: "",
    description: "",
    project_id: ""
  });

  useEffect(() => {
    fetchTickets();
    fetchProjects();
  }, [filterStatus]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("status", filterStatus);
      
      const response = await axios.get(`${API}/tickets?${params.toString()}`);
      setTickets(response.data);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!newTicket.type || !newTicket.title || !newTicket.description) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      const payload = {
        ...newTicket,
        client_id: user.client_id || ""
      };
      
      await axios.post(`${API}/tickets`, payload);
      toast.success("Ticket créé avec succès");
      setDialogOpen(false);
      setNewTicket({ type: "", priority: "medium", title: "", description: "", project_id: "" });
      fetchTickets();
    } catch (error) {
      console.error("Error creating ticket:", error);
      toast.error("Erreur lors de la création du ticket");
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { status: newStatus });
      toast.success("Statut mis à jour");
      fetchTickets();
    } catch (error) {
      console.error("Error updating ticket:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tickets-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isAdmin ? "Tous les Tickets" : "Mes Tickets"}
          </h1>
          <p className="text-[#94A3B8] mt-1">
            {tickets.length} ticket{tickets.length > 1 ? 's' : ''}
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0099CC] hover:bg-[#0088B5] text-white btn-glow" data-testid="create-ticket-btn">
              <Plus className="mr-2 h-4 w-4" />
              Nouveau Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Créer un nouveau ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Type de demande *</Label>
                <Select value={newTicket.type} onValueChange={(v) => setNewTicket({...newTicket, type: v})}>
                  <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white">
                    <SelectValue placeholder="Sélectionnez un type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151B2B] border-[#2A3441]">
                    {ticketTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Priorité</Label>
                <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({...newTicket, priority: v})}>
                  <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151B2B] border-[#2A3441]">
                    <SelectItem value="low">Basse</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {projects.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">Projet concerné</Label>
                  <Select value={newTicket.project_id} onValueChange={(v) => setNewTicket({...newTicket, project_id: v})}>
                    <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white">
                      <SelectValue placeholder="Sélectionnez un projet" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151B2B] border-[#2A3441]">
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Titre *</Label>
                <Input
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                  placeholder="Résumez votre demande"
                  className="bg-[#0B0F19] border-[#2A3441] text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Description *</Label>
                <Textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                  placeholder="Décrivez votre demande en détail..."
                  className="bg-[#0B0F19] border-[#2A3441] text-white min-h-[120px]"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-[#2A3441] text-[#94A3B8]">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white">
                  Créer le ticket
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] bg-[#151B2B] border-[#2A3441] text-white">
            <Filter className="h-4 w-4 mr-2 text-[#64748b]" />
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-[#151B2B] border-[#2A3441]">
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="open">Ouverts</SelectItem>
            <SelectItem value="in_progress">En cours</SelectItem>
            <SelectItem value="resolved">Résolus</SelectItem>
            <SelectItem value="closed">Fermés</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-20 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <Ticket className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun ticket</h3>
          <p className="text-[#94A3B8] mb-6">
            {filterStatus !== "all" ? "Aucun ticket avec ce statut" : "Créez votre premier ticket"}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => {
            const status = statusConfig[ticket.status] || statusConfig.open;
            const StatusIcon = status.icon;
            
            return (
              <div
                key={ticket.id}
                onClick={() => navigate(`/app/tickets/${ticket.id}`)}
                className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-6 card-premium cursor-pointer hover:border-[#0099CC]/40 transition-colors"
                data-testid={`ticket-${ticket.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${status.bg}`}>
                        <StatusIcon className={`h-4 w-4 ${status.color}`} />
                      </div>
                      <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
                      <Badge className={priorityColors[ticket.priority]}>
                        {ticket.priority === 'high' ? 'Haute' : ticket.priority === 'medium' ? 'Moyenne' : 'Basse'}
                      </Badge>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-white mb-2">{ticket.title}</h3>
                    <p className="text-[#94A3B8] text-sm mb-3 line-clamp-2">{ticket.description}</p>
                    
                    <div className="flex flex-wrap gap-3 text-xs text-[#64748b]">
                      <span className="bg-[#2A3441] px-2 py-1 rounded">
                        {ticketTypes.find(t => t.value === ticket.type)?.label || ticket.type}
                      </span>
                      <span>{formatDate(ticket.created_at)}</span>
                    </div>
                  </div>

                  {/* Admin actions */}
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <Select value={ticket.status} onValueChange={(v) => { v && handleUpdateStatus(ticket.id, v); }}>
                        <SelectTrigger className="w-[150px] bg-[#0B0F19] border-[#2A3441] text-white text-sm" onClick={(e) => e.stopPropagation()}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#151B2B] border-[#2A3441]">
                          <SelectItem value="open">Ouvert</SelectItem>
                          <SelectItem value="in_progress">En cours</SelectItem>
                          <SelectItem value="resolved">Resolu</SelectItem>
                          <SelectItem value="closed">Ferme</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <ChevronRight className="h-5 w-5 text-[#64748b]" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
